import {
  GeneralPermissions,
  SettingsPermissions,
} from "@redotech/account-common/permissions";
import { TeamType } from "@redotech/account-common/team-type";
import { ReturnsSettingsTab } from "../flat-team-settings/returns/returns-settings-tab";
import {
  all,
  always,
  any,
  capability,
  evaluateSettingsCondition,
  experience,
  fact,
  flag,
  not,
  permission,
  settingsAccessVerdict,
  type SettingsCondition,
  type SettingsUserFacts,
  type SettingsVisibilityFacts,
  shopifyPlatform,
  staff,
  teamType,
} from "./conditions";

/** Settings nav sections, keyed by the nav's section ids. */
export type SettingsSectionId =
  | "general"
  | "personal"
  | "ai"
  | "conversion"
  | "chargebacks"
  | "customer-accounts"
  | "marketing"
  | "order-tracking"
  | "payments"
  | "returns-and-claims"
  | "products"
  | "warranties"
  | "inventory-management"
  | "order-fulfillment"
  | "shipping"
  | "international"
  | "support"
  | "supplies";

interface SettingsSectionRule {
  requires: SettingsCondition;
}

interface SettingsPageRule {
  requires: SettingsCondition;
}

const shippingNew = experience("shippingNewSettings");
const omsFulfillment = all(
  capability("oms"),
  permission(SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS),
);
const shippingAllowed = all(
  permission(SettingsPermissions.SET_SHIPPING_SETTINGS),
  not(fact("restrictedShippingTeam")),
);
const manageChatWidget = any(
  permission(SettingsPermissions.SET_SUPPORT_SETTINGS),
  permission(SettingsPermissions.SET_AI_SETTINGS),
  permission(SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS),
  permission(SettingsPermissions.SET_GENERAL_SETTINGS),
);
const returnsPermission = permission(
  SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
);
const generalPermission = permission(SettingsPermissions.SET_GENERAL_SETTINGS);
const returnsLegacy = experience("returnsLegacyUi");
const returnsOrClaims = any(
  capability("returns"),
  capability("packageProtection"),
);
const reverseLogistics = any(
  capability("returns"),
  capability("packageProtection"),
  capability("warranties"),
  capability("withdrawals"),
  capability("recycling"),
);
const emailsCustomers = any(
  capability("returns"),
  capability("packageProtection"),
  capability("warranties"),
  capability("withdrawals"),
  capability("orderTracking"),
  capability("emailSms"),
  capability("support"),
  capability("recover"),
);
/** AI turned on in admin; billing gates whether AI runs, not whether it can be configured. */
const aiTurnedOn = any(capability("ai"), fact("supportAiSetting"));
/** Products whose storefront recommendations read AI General's product recommendation settings. */
const usesProductRecommendations = any(
  capability("emailSms"),
  capability("orderTracking"),
  capability("warranties"),
  capability("orderEditing"),
);
const buysLabels = any(
  capability("oms"),
  capability("wms"),
  capability("returns"),
  capability("packageProtection"),
);

/** Any permission that opens at least one integration; must match the integrations catalog. */
export const INTEGRATIONS_HUB_SETTINGS_PERMISSIONS = [
  SettingsPermissions.SET_CHECKOUT_OPTIMIZATION_SETTINGS,
  SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS,
  SettingsPermissions.SET_GENERAL_SETTINGS,
  SettingsPermissions.SET_MARKETING_SETTINGS,
  SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
  SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  SettingsPermissions.SET_SHIPPING_SETTINGS,
  SettingsPermissions.SET_SUPPORT_SETTINGS,
] as const;

export const SETTINGS_SECTIONS = {
  general: { requires: always },
  personal: { requires: always },
  ai: {
    requires: all(
      any(aiTurnedOn, usesProductRecommendations),
      permission(SettingsPermissions.SET_AI_SETTINGS),
    ),
  },
  conversion: { requires: always },
  chargebacks: { requires: all(capability("disputes"), generalPermission) },
  "customer-accounts": {
    requires: all(
      capability("customerAccounts"),
      permission(SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS),
    ),
  },
  marketing: {
    requires: all(
      any(
        capability("returns"),
        capability("emailSms"),
        capability("orderTracking"),
      ),
      permission(SettingsPermissions.SET_MARKETING_SETTINGS),
    ),
  },
  "order-tracking": {
    requires: all(
      capability("orderTracking"),
      permission(SettingsPermissions.SET_MARKETING_SETTINGS),
    ),
  },
  payments: {
    requires: all(
      capability("hsa"),
      permission(SettingsPermissions.SET_CHECKOUT_OPTIMIZATION_SETTINGS),
    ),
  },
  "returns-and-claims": {
    requires: all(
      returnsPermission,
      any(
        reverseLogistics,
        teamType(TeamType.FulfillmentService),
        fact("wmsSourceOfTruth"),
      ),
    ),
  },
  products: { requires: all(generalPermission, buysLabels) },
  warranties: {
    requires: all(
      capability("warranties"),
      permission(SettingsPermissions.SET_WARRANTY_SETTINGS),
    ),
  },
  "inventory-management": {
    requires: all(capability("ims"), permission(GeneralPermissions.OTHER)),
  },
  "order-fulfillment": {
    requires: any(
      omsFulfillment,
      generalPermission,
      all(shippingNew, permission(SettingsPermissions.SET_SHIPPING_SETTINGS)),
    ),
  },
  shipping: {
    requires: any(
      all(not(shippingNew), shippingAllowed),
      all(
        shippingNew,
        any(
          permission(SettingsPermissions.SET_SHIPPING_SETTINGS),
          omsFulfillment,
        ),
      ),
    ),
  },
  international: {
    requires: any(
      permission(SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS),
      returnsPermission,
      permission(SettingsPermissions.SET_SHIPPING_SETTINGS),
    ),
  },
  support: {
    requires: all(
      capability("support"),
      any(
        permission(SettingsPermissions.SET_SUPPORT_SETTINGS),
        manageChatWidget,
      ),
    ),
  },
  supplies: {
    requires: all(
      fact("redoSuppliesTeam"),
      permission(GeneralPermissions.OTHER),
    ),
  },
} as const satisfies Record<SettingsSectionId, SettingsSectionRule>;

/** Every settings page and who sees it. Keys are `<section>/<page>`. */
export const SETTINGS_PAGES = {
  "general/billing": {
    requires: all(
      not(fact("shopifyEmailTeam")),
      any(
        generalPermission,
        permission(SettingsPermissions.VIEW_BILLING),
        permission(SettingsPermissions.SET_BILLING_SETTINGS),
      ),
    ),
  },
  "general/billing-home": { requires: always },
  "general/billing-activity": { requires: always },
  "general/bills": { requires: always },
  "general/brand-kit": { requires: generalPermission },
  "general/communication": {
    requires: all(generalPermission, emailsCustomers),
  },
  "general/customer-portal": {
    requires: all(
      reverseLogistics,
      any(generalPermission, returnsPermission),
      not(all(not(returnsLegacy), returnsPermission)),
    ),
  },
  "general/portal-builder": { requires: always },
  "general/point-of-sale": {
    requires: all(capability("returns"), returnsPermission, shopifyPlatform),
  },
  "general/merchant-created-returns": {
    requires: all(
      capability("returns"),
      returnsPermission,
      permission(GeneralPermissions.MANAGE_TEAM_ROLES),
    ),
  },
  "general/customer-widget": {
    requires: all(
      not(capability("support")),
      any(fact("supportAiSetting"), capability("customerAccounts")),
      manageChatWidget,
    ),
  },
  "general/developer": {
    requires: permission(GeneralPermissions.MANAGE_TEAM_DEVELOPMENT),
  },
  "general/integrations": {
    requires: any(...INTEGRATIONS_HUB_SETTINGS_PERMISSIONS.map(permission)),
  },
  "general/notifications": { requires: generalPermission },
  "general/roles": {
    requires: permission(GeneralPermissions.MANAGE_TEAM_ROLES),
  },
  "general/users": {
    requires: permission(GeneralPermissions.MANAGE_TEAM_USERS),
  },
  "general/user-groups": {
    requires: permission(GeneralPermissions.MANAGE_TEAM_USERS),
  },
  "personal/profile": { requires: always },
  "ai/general": { requires: always },
  "ai/knowledge": { requires: aiTurnedOn },
  "ai/skills": { requires: aiTurnedOn },
  "conversion/general": {
    requires: all(
      capability("checkoutOptimization"),
      permission(SettingsPermissions.SET_CHECKOUT_OPTIMIZATION_SETTINGS),
    ),
  },
  "payments/hsa": { requires: always },
  "chargebacks/chargebacks": { requires: always },
  "customer-accounts/ad-blocks": { requires: always },
  "customer-accounts/storefront-display": { requires: always },
  "supplies/shipping-rules": { requires: always },
  "inventory-management/inventory-management": { requires: shippingNew },
  "inventory-management/general": { requires: not(shippingNew) },
  "inventory-management/purchase-orders": { requires: not(shippingNew) },
  "inventory-management/sales-channels": { requires: not(shippingNew) },
  "inventory-management/accounting": {
    requires: all(not(shippingNew), flag("imsQuickbooks")),
  },
  "order-fulfillment/order-management": {
    requires: all(shippingNew, any(omsFulfillment, shippingAllowed)),
  },
  "order-fulfillment/general": {
    requires: all(not(shippingNew), omsFulfillment),
  },
  "order-fulfillment/auto-merge": {
    requires: all(not(shippingNew), omsFulfillment),
  },
  "order-fulfillment/automations": { requires: omsFulfillment },
  "order-fulfillment/custom-imports": {
    requires: all(not(shippingNew), omsFulfillment),
  },
  "order-fulfillment/international": {
    requires: all(not(shippingNew), omsFulfillment),
  },
  "order-fulfillment/order-editing": { requires: generalPermission },
  "order-fulfillment/packages": {
    requires: all(not(shippingNew), omsFulfillment),
  },
  "order-fulfillment/packing-slips": {
    requires: all(shippingNew, omsFulfillment),
  },
  "order-fulfillment/presets": {
    requires: all(not(shippingNew), omsFulfillment),
  },
  "order-fulfillment/printing": {
    requires: all(not(shippingNew), omsFulfillment),
  },
  "order-fulfillment/reports": { requires: omsFulfillment },
  "order-fulfillment/scanning-warehouse": { requires: omsFulfillment },
  "order-fulfillment/warehouse-dashboard": { requires: omsFulfillment },
  "order-fulfillment/security": {
    requires: all(not(shippingNew), omsFulfillment),
  },
  "order-fulfillment/warehouse-layouts": { requires: omsFulfillment },
  "order-fulfillment/work-order-flows": { requires: omsFulfillment },
  "marketing/audience": { requires: capability("emailSms") },
  "marketing/email-domain": { requires: emailsCustomers },
  "marketing/offer-page": { requires: capability("emailSms") },
  "marketing/sms": { requires: capability("emailSms") },
  "marketing/keywords": { requires: capability("emailSms") },
  "marketing/recover": { requires: capability("recover") },
  "marketing/attribution": {
    requires: all(capability("emailSms"), flag("marketingAttribution")),
  },
  "marketing/marketing-agent": { requires: capability("emailSms") },
  "order-tracking/general": { requires: always },
  "products/attributes": { requires: staff },
  "products/products": { requires: always },
  "returns-and-claims/returns": { requires: experience("returnsNewUi") },
  "returns-and-claims/returns/coverage": { requires: returnsOrClaims },
  "returns-and-claims/returns/policies": { requires: always },
  "returns-and-claims/returns/portal": { requires: always },
  "returns-and-claims/returns/operations": { requires: always },
  "returns-and-claims/returns/labels": { requires: always },
  "returns-and-claims/returns/locations": { requires: always },
  "returns-and-claims/returns/shopper-notifications": { requires: always },
  "returns-and-claims/returns/warehouse": { requires: always },
  "returns-and-claims/ab-tests": { requires: staff },
  "returns-and-claims/assessments": { requires: returnsOrClaims },
  "returns-and-claims/automations": { requires: returnsLegacy },
  "returns-and-claims/coverage": {
    requires: all(returnsLegacy, returnsOrClaims),
  },
  "returns-and-claims/exchanges": {
    requires: all(returnsLegacy, capability("returns")),
  },
  "returns-and-claims/processing": { requires: returnsLegacy },
  "returns-and-claims/refunds": { requires: returnsLegacy },
  "returns-and-claims/processing-groups": {
    requires: teamType(TeamType.FulfillmentService),
  },
  "returns-and-claims/post-processing": {
    requires: any(
      teamType(TeamType.FulfillmentService),
      fact("wmsSourceOfTruth"),
    ),
  },
  "returns-and-claims/consolidation": {
    requires: teamType(TeamType.FulfillmentService),
  },
  "returns-and-claims/shipping": { requires: returnsLegacy },
  "returns-and-claims/return-detail-view": { requires: returnsLegacy },
  "returns-and-claims/warehouse": { requires: returnsLegacy },
  "returns-and-claims/withdrawals": {
    requires: any(returnsOrClaims, capability("withdrawals")),
  },
  "warranties/registration": { requires: always },
  "warranties/registration-rewards": {
    requires: fact("warrantyRegistrationRewards"),
  },
  "international/markets": {
    requires: all(
      permission(SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS),
      capability("landedCost"),
    ),
  },
  "international/restrictions": {
    requires: all(
      permission(SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS),
      capability("landedCost"),
    ),
  },
  "international/returns": {
    requires: all(
      any(returnsOrClaims, capability("landedCost")),
      any(
        returnsPermission,
        permission(SettingsPermissions.SET_SHIPPING_SETTINGS),
      ),
    ),
  },
  "shipping/carriers": {
    requires: all(
      shippingAllowed,
      any(buysLabels, capability("checkoutOptimization")),
    ),
  },
  "shipping/fulfillment-promise": {
    requires: all(
      not(shippingNew),
      shippingAllowed,
      any(capability("oms"), capability("checkoutOptimization")),
    ),
  },
  "shipping/freight": {
    requires: all(shippingAllowed, any(capability("oms"), capability("wms"))),
  },
  "support/general": {
    requires: permission(SettingsPermissions.SET_SUPPORT_SETTINGS),
  },
  "support/customer-widget": {
    requires: all(capability("support"), manageChatWidget),
  },
  "support/channels": {
    requires: permission(SettingsPermissions.SET_SUPPORT_SETTINGS),
  },
  "support/tags": {
    requires: permission(SettingsPermissions.SET_SUPPORT_SETTINGS),
  },
  "support/macros": {
    requires: permission(SettingsPermissions.SET_SUPPORT_SETTINGS),
  },
  "support/topics": {
    requires: permission(SettingsPermissions.SET_SUPPORT_SETTINGS),
  },
  "support/rules": {
    requires: permission(SettingsPermissions.SET_SUPPORT_SETTINGS),
  },
} as const satisfies Record<`${SettingsSectionId}/${string}`, SettingsPageRule>;

export type SettingsPageId = keyof typeof SETTINGS_PAGES;

/** Nav child pages, shown only when their parent page is. */
const SETTINGS_PAGE_PARENTS: Partial<Record<SettingsPageId, SettingsPageId>> = {
  "general/billing-home": "general/billing",
  "general/billing-activity": "general/billing",
  "general/bills": "general/billing",
  "general/portal-builder": "general/customer-portal",
  "general/point-of-sale": "general/customer-portal",
  "general/merchant-created-returns": "general/customer-portal",
  "returns-and-claims/returns/coverage": "returns-and-claims/returns",
  "returns-and-claims/returns/policies": "returns-and-claims/returns",
  "returns-and-claims/returns/portal": "returns-and-claims/returns",
  "returns-and-claims/returns/operations": "returns-and-claims/returns",
  "returns-and-claims/returns/labels": "returns-and-claims/returns",
  "returns-and-claims/returns/locations": "returns-and-claims/returns",
  "returns-and-claims/returns/shopper-notifications":
    "returns-and-claims/returns",
  "returns-and-claims/returns/warehouse": "returns-and-claims/returns",
};

/** The page followed by its nav parents, nearest first. */
export function settingsPageLineage(page: SettingsPageId): SettingsPageId[] {
  const parent = SETTINGS_PAGE_PARENTS[page];
  return [page, ...(parent ? settingsPageLineage(parent) : [])];
}

/** The page's own rule, then its parent's, then its section's. */
function settingsPageConditions(page: SettingsPageId): SettingsCondition[] {
  const parent = SETTINGS_PAGE_PARENTS[page];
  return [
    SETTINGS_PAGES[page].requires,
    ...(parent
      ? settingsPageConditions(parent)
      : [SETTINGS_SECTIONS[sectionOfSettingsPage(page)].requires]),
  ];
}

function isSettingsSectionId(id: string): id is SettingsSectionId {
  return Object.hasOwn(SETTINGS_SECTIONS, id);
}

export function sectionOfSettingsPage(page: SettingsPageId): SettingsSectionId {
  const id = page.slice(0, page.indexOf("/"));
  if (!isSettingsSectionId(id)) {
    throw new Error(`Settings page ${page} has no section`);
  }
  return id;
}

export function isSettingsSectionVisible(
  section: SettingsSectionId,
  facts: SettingsVisibilityFacts,
): boolean {
  return evaluateSettingsCondition(SETTINGS_SECTIONS[section].requires, facts);
}

/** A page is visible when its own, parent and section rules all pass. */
export function isSettingsPageVisible(
  page: SettingsPageId,
  facts: SettingsVisibilityFacts,
): boolean {
  return settingsPageConditions(page).every((condition) =>
    evaluateSettingsCondition(condition, facts),
  );
}

/** Route guards: whether this user could open any of the pages on some team. */
export function canOpenSettingsPages(
  pages: readonly SettingsPageId[],
  user: SettingsUserFacts,
): boolean {
  return pages.some((page) =>
    settingsPageConditions(page).every(
      (condition) => settingsAccessVerdict(condition, user) !== "denied",
    ),
  );
}

/** Each tab on the consolidated Returns page is a registry page. */
export const RETURNS_SETTINGS_TAB_PAGES = {
  [ReturnsSettingsTab.COVERAGE]: "returns-and-claims/returns/coverage",
  [ReturnsSettingsTab.POLICIES]: "returns-and-claims/returns/policies",
  [ReturnsSettingsTab.PORTAL]: "returns-and-claims/returns/portal",
  [ReturnsSettingsTab.OPERATIONS]: "returns-and-claims/returns/operations",
  [ReturnsSettingsTab.LABELS]: "returns-and-claims/returns/labels",
  [ReturnsSettingsTab.LOCATIONS]: "returns-and-claims/returns/locations",
  [ReturnsSettingsTab.SHOPPER_NOTIFICATIONS]:
    "returns-and-claims/returns/shopper-notifications",
  [ReturnsSettingsTab.WAREHOUSE]: "returns-and-claims/returns/warehouse",
} as const satisfies Record<ReturnsSettingsTab, SettingsPageId>;
